import { FC, ReactNode } from "react"
import clsx from "clsx"

import { useTranslation } from "react-i18next"

import { PurpleToggle } from "components/toggles/PurpleToggle"

import { PLAYER_LOCALE_NAMES, PLAYER_LOCALES } from "constants/player-locales"

import { CMS_LOCALE_NAMES, CmsPageStatus, CmsPageType } from "services/cms-seo/type"

import { scoreLevel, SeoCheckLevel } from "./helpers/seoScore"

const STATUS_CLASSES: Record<CmsPageStatus, string> = {
  published: "bg-emerald-500/[0.13] text-emerald-500",
  draft: "bg-gray-500/[0.14] text-gray-400",
  scheduled: "bg-amber-500/[0.13] text-amber-500"
}

const TYPE_CLASSES: Record<CmsPageType, string> = {
  page: "bg-blue-500/[0.13] text-blue-400",
  article: "bg-purple-500/[0.15] text-purple-300",
  landing: "bg-emerald-500/[0.11] text-emerald-400"
}

export const StatusBadge: FC<{ status: CmsPageStatus }> = ({ status }) => (
  <span className={clsx("inline-block rounded-full px-2.5 py-0.5 text-[11px] font-semibold tracking-wide", STATUS_CLASSES[status])}>
    {status.toUpperCase()}
  </span>
)

export const TypeBadge: FC<{ type: CmsPageType }> = ({ type }) => (
  <span className={clsx("inline-block rounded-full px-2.5 py-0.5 text-[11px] font-semibold tracking-wide", TYPE_CLASSES[type])}>
    {type.toUpperCase()}
  </span>
)

export const CodeBadge: FC<{ code: number }> = ({ code }) => (
  <span
    className={clsx(
      "inline-block rounded-full px-2.5 py-0.5 text-[11px] font-semibold",
      code === 301 ? "bg-emerald-500/[0.13] text-emerald-500" : "bg-amber-500/[0.13] text-amber-500"
    )}
  >
    {code}
  </span>
)

const LEVEL_TEXT: Record<SeoCheckLevel, string> = {
  ok: "text-emerald-500",
  warn: "text-amber-500",
  bad: "text-rose-500"
}

const LEVEL_DOT: Record<SeoCheckLevel, string> = {
  ok: "bg-emerald-500",
  warn: "bg-amber-500",
  bad: "bg-rose-500"
}

export const ScoreChip: FC<{ passed: number; total: number }> = ({ passed, total }) => {
  const level = scoreLevel(passed, total)
  return (
    <span className={clsx("inline-flex items-center gap-1.5 text-xs font-semibold", LEVEL_TEXT[level])}>
      <i className={clsx("h-1.5 w-1.5 rounded-full not-italic", LEVEL_DOT[level])} />
      {passed}/{total}
    </span>
  )
}

export const IndexFlag: FC<{ indexed: boolean }> = ({ indexed }) => (
  <span className={clsx("text-xs font-medium", indexed ? "text-emerald-500" : "text-gray-500")}>{indexed ? "Indexed" : "Noindex"}</span>
)

interface LocaleChipsProps {
  locales: string[]
  missing: string[]
  /** Creates the missing language from this row, without opening the page first. */
  onCreate?: (locale: string) => void
}

export const LocaleChips: FC<LocaleChipsProps> = ({ locales, missing, onCreate }) => (
  <span className="flex flex-wrap gap-1">
    {locales.map((locale) => (
      <span
        key={locale}
        title={CMS_LOCALE_NAMES[locale] ?? locale}
        className="rounded bg-black/[0.06] px-1.5 py-0.5 text-[11px] font-semibold uppercase text-gray-600 dark:bg-white/[0.07] dark:text-gray-300"
      >
        {locale}
      </span>
    ))}
    {missing.map((locale) => (
      <button
        key={locale}
        type="button"
        disabled={!onCreate}
        title={`Create the ${CMS_LOCALE_NAMES[locale] ?? locale} version`}
        onClick={(event) => {
          event.stopPropagation()
          onCreate?.(locale)
        }}
        className={clsx(
          "rounded border border-dashed border-gray-400/40 px-1.5 py-0.5 text-[11px] font-semibold uppercase text-gray-400",
          onCreate && "hover:border-purple-500 hover:text-purple-500"
        )}
      >
        +{locale}
      </button>
    ))}
  </span>
)

export const PathText: FC<{ children: ReactNode; className?: string }> = ({ children, className }) => (
  <span className={clsx("font-mono text-xs text-purple-500 dark:text-[#cdbdfb]", className)}>{children}</span>
)

export const SectionHeading: FC<{ children: ReactNode }> = ({ children }) => (
  <h3 className="mt-7 mb-3.5 text-xs font-semibold tracking-wide text-gray-500 uppercase first:mt-0 dark:text-gray-400">{children}</h3>
)

interface FieldProps {
  label: string
  counter?: ReactNode
  hint?: ReactNode
  className?: string
  children: ReactNode
}

export const Field: FC<FieldProps> = ({ label, counter, hint, className, children }) => (
  <div className={clsx("flex min-w-[200px] flex-1 flex-col gap-1.5", className)}>
    <label className="flex items-center justify-between text-xs font-medium text-gray-500 dark:text-gray-400">
      <span>{label}</span>
      {counter}
    </label>
    {children}
    {hint && <p className="mt-1 text-[11.5px] leading-relaxed text-gray-400 dark:text-gray-500">{hint}</p>}
  </div>
)

export const Counter: FC<{ length: number; limit: number }> = ({ length, limit }) => (
  <span className={clsx("text-[11.5px] font-medium", length > limit ? "text-amber-500" : "text-gray-400 dark:text-gray-500")}>
    {length} / {limit}
  </span>
)

export const FieldRow: FC<{ children: ReactNode; className?: string }> = ({ children, className }) => (
  <div className={clsx("mb-5 flex flex-wrap gap-4", className)}>{children}</div>
)

export const inputClass =
  "w-full min-h-[38px] rounded-lg border px-3 py-2 text-sm bg-white text-black border-gray-200 " +
  "dark:bg-[#0C0C1A] dark:text-white dark:border-[rgba(255,255,255,0.12)] " +
  "focus:outline-none focus:border-[rgba(169,78,206,0.8)] disabled:opacity-50"

interface ToggleRowProps {
  title: string
  description?: ReactNode
  checked: boolean
  onChange: () => void
  disabled?: boolean
}

export const ToggleRow: FC<ToggleRowProps> = ({ title, description, checked, onChange, disabled }) => (
  <div className="flex items-center gap-3 border-b border-black/[0.06] py-3 last:border-b-0 dark:border-white/[0.06]">
    <div className="flex-1">
      <b className="block text-sm font-medium text-black dark:text-white">{title}</b>
      {description && <span className="text-xs text-gray-500 dark:text-gray-400">{description}</span>}
    </div>
    <PurpleToggle checked={checked} onChange={onChange} disabled={disabled} />
  </div>
)

export const InfoBox: FC<{ children: ReactNode }> = ({ children }) => (
  <div className="mb-5 rounded-xl border border-black/[0.06] bg-black/[0.02] p-4 text-xs leading-relaxed text-gray-600 dark:border-white/[0.08] dark:bg-white/[0.03] dark:text-gray-300">
    {children}
  </div>
)

export const WarnBox: FC<{ children: ReactNode }> = ({ children }) => (
  <div className="mb-5 flex gap-3 rounded-xl border border-amber-500/30 bg-amber-500/[0.08] p-3.5 text-xs leading-relaxed text-amber-700 dark:text-amber-200">
    <span className="text-[15px] leading-none text-amber-500">⚠</span>
    <p className="m-0">{children}</p>
  </div>
)

export const LockBox: FC<{ children: ReactNode }> = ({ children }) => (
  <div className="mb-5 flex gap-3 rounded-xl border border-rose-500/30 bg-rose-500/[0.08] p-3.5 text-xs leading-relaxed text-rose-700 dark:text-rose-200">
    <span className="text-[15px] leading-none">🔒</span>
    <p className="m-0">{children}</p>
  </div>
)

export const RailRow: FC<{ label: ReactNode; value: ReactNode }> = ({ label, value }) => (
  <div className="flex items-center justify-between border-b border-black/[0.06] py-2 text-[13px] last:border-b-0 dark:border-white/[0.06]">
    <span className="text-gray-500 dark:text-gray-400">{label}</span>
    <span className="text-black dark:text-white">{value}</span>
  </div>
)

/** Bounded and scrollable: the wrapper centres the dialog at its content's height. */
export const modalBoxClass =
  "modal-box max-h-[90vh] w-full overflow-y-auto bg-white text-black dark:bg-[#131421] dark:text-white"

export const primaryButtonClass =
  "btn btn-sm border-transparent bg-[rgba(132,0,168,1)] text-white hover:bg-[rgba(169,78,206,1)] normal-case"

export const ghostButtonClass =
  "btn btn-sm border-black/[0.1] bg-transparent text-black hover:bg-black/[0.05] dark:border-white/[0.12] dark:text-white dark:hover:bg-white/[0.06] normal-case"

/** Shared so a selected chip and a primary button cannot drift. */
const BRAND_SURFACE = "bg-[rgba(132,0,168,1)] text-white"

export const dangerButtonClass =
  "btn btn-sm border-transparent bg-rose-600/80 text-white hover:bg-rose-600 normal-case"

interface FilterFieldProps {
  label: string
  /** Wider, for free text rather than a fixed set. */
  grow?: boolean
  children: ReactNode
}

/** A `label`, so the text focuses the control. */
export const FilterField: FC<FilterFieldProps> = ({ label, grow, children }) => (
  <label className={clsx("flex flex-col gap-1.5", grow ? "min-w-[220px] flex-1" : "min-w-[150px]")}>
    <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</span>
    {children}
  </label>
)

interface AsyncListProps {
  loading: boolean
  isEmpty: boolean
  emptyText: string
  children: ReactNode
}

/** An empty list rendering a bare table header reads as broken, not as empty. */
export const AsyncList: FC<AsyncListProps> = ({ loading, isEmpty, emptyText, children }) => {
  const { t } = useTranslation("translation", { useSuspense: false })

  if (loading) {
    return (
      <p className="py-8 text-center text-sm text-black dark:text-white">
        {t("common.loading", { defaultValue: "Loading..." })}
      </p>
    )
  }

  if (isEmpty) {
    return <p className="py-8 text-center text-sm opacity-70 text-black dark:text-white">{emptyText}</p>
  }

  return <>{children}</>
}

interface LocaleChipBarProps {
  /** The language being edited. */
  current: string
  /** Languages that already have something of their own. */
  present: readonly string[]
  onSelect: (locale: string) => void
  /** Omit to make a missing language unselectable. */
  onCreate?: (locale: string) => void
  busy?: boolean
  /** Shown from lg up. */
  hint?: string
}

/** One row, so there is one answer to "which languages does this exist in". */
export const LocaleChipBar: FC<LocaleChipBarProps> = ({
  current,
  present,
  onSelect,
  onCreate,
  busy,
  hint
}) => {
  const { t } = useTranslation("translation", { useSuspense: false })

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="mr-1 text-xs font-medium text-gray-500 dark:text-gray-400">
        {t("cmsSeo.languages", { defaultValue: "Languages" })}
      </span>

      {PLAYER_LOCALES.map((locale) => {
        const isCurrent = locale === current
        const exists = present.includes(locale)
        const name = PLAYER_LOCALE_NAMES[locale] ?? locale

        return (
          <button
            key={locale}
            type="button"
            disabled={busy || (!exists && !isCurrent && !onCreate)}
            title={exists ? name : `${name} — ${t("cmsSeo.notTranslatedYet", { defaultValue: "not translated yet" })}`}
            onClick={() => (exists || isCurrent ? onSelect(locale) : onCreate?.(locale))}
            className={clsx(
              "rounded-md px-2 py-1 text-[11px] font-semibold uppercase disabled:opacity-40",
              isCurrent && BRAND_SURFACE,
              !isCurrent &&
                exists &&
                "bg-black/[0.06] text-gray-600 hover:bg-black/[0.1] dark:bg-white/[0.08] dark:text-gray-200 dark:hover:bg-white/[0.14]",
              !isCurrent &&
                !exists &&
                "border border-dashed border-gray-400/50 text-gray-400 hover:border-purple-500 hover:text-purple-500"
            )}
          >
            {exists || isCurrent ? locale : `+ ${locale}`}
          </button>
        )
      })}

      {hint && <span className="ml-auto hidden text-[11px] text-gray-400 lg:inline dark:text-gray-500">{hint}</span>}
    </div>
  )
}
